import Vue from 'vue';
import VueRouter from 'vue-router';
import store from '../store';
import Layout from '@/views/common/Layout.vue';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';

Vue.use(VueRouter);

NProgress.configure({ showSpinner: false });

// 白名单
const whiteList = ['/login', '/404'];

// 常量路由（所有人可见）
export const constantRoutes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/common/Login.vue'),
    hidden: true
  },
  {
    path: '/404',
    name: 'NotFound',
    component: () => import('../views/common/NotFound.vue'),
    hidden: true
  },
  {
    path: '/',
    name: 'Layout',
    component: Layout,
    redirect: '/home',
    children: [
      {
        path: 'home',
        name: 'Home',
        component: () => import('../views/common/Home.vue'),
        meta: { title: '首页' }
      },
      {
        path: 'notifications',
        name: 'MyNotifications',
        component: () => import('../views/admin/Notifications.vue'),
        meta: { title: '通知中心' }
      },
      {
        path: 'manage/order-history',
        name: 'OrderHistory',
        component: () => import('@/views/admin/OrderHistory.vue'),
        meta: { 
          title: '历史订单'
        }
      }
    ]
  }
  // 注意：通配符路由 * 不在这里定义，而是在动态路由加载后添加
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: constantRoutes
});

// 捕获重定向错误（这是 Vue Router 3.x 的已知问题）
const originalPush = router.push;
router.push = function push(location) {
  return originalPush.call(this, location).catch(err => {
    if (err.name === 'NavigationDuplicated' || err.message.includes('Redirected')) {
      // 忽略重定向和重复导航错误
      return Promise.resolve();
    }
    return Promise.reject(err);
  });
};

// 同样处理 replace
const originalReplace = router.replace;
router.replace = function replace(location) {
  return originalReplace.call(this, location).catch(err => {
    if (err.name === 'NavigationDuplicated' || err.message.includes('Redirected')) {
      return Promise.resolve();
    }
    return Promise.reject(err);
  });
};

// 导航锁，防止重复加载
let isLoadingMenus = false;

// 标记是否已添加404通配符路由
let is404RouteAdded = false;

// 添加404通配符路由（只添加一次）
const add404Route = () => {
  if (is404RouteAdded) return;
  
  router.addRoute({
    path: '*',
    redirect: '/404',
    hidden: true
  });
  
  is404RouteAdded = true;
};

// 路由守卫
router.beforeEach(async (to, from, next) => {
  NProgress.start();

  // 如果正在加载菜单，直接放行避免冲突
  if (isLoadingMenus) {
    return next();
  }

  const token = store.getters.token;
  const hasLoaded = store.state.menus?.hasLoaded;

  if (token) {
    // 已登录
    if (to.path === '/login') {
      next('/');
      NProgress.done();
    } else {
      // 判断是否已加载菜单（用 hasLoaded 判断）
      if (!hasLoaded) {
        isLoadingMenus = true; // 加锁
        try {
          // 从后端获取用户菜单并生成路由
          const accessRoutes = await store.dispatch('menus/fetchUserMenus')

          // 动态添加路由作为 Layout 的子路由
          accessRoutes.forEach(route => router.addRoute('Layout', route));

          // 动态路由加载完成后，添加404通配符路由
          add404Route();

          // 重新导航到目标页面，使用 replace 避免历史记录问题
          next({ ...to, replace: true });
        } catch (error) {
          console.error('加载菜单失败:', error)
          await store.dispatch('logout')
          next('/login')
          NProgress.done()
        } finally {
          isLoadingMenus = false; // 解锁
        }
      } else {
        // hasLoaded 为 true（可能是持久化恢复的）
        // 检查是否有动态路由数据，如果没有需要重新生成（刷新页面后路由丢失）
        if (store.state.menus?.menuList?.length > 0 && store.state.menus?.dynamicRoutes?.length === 0) {
          try {
            // 从持久化的 menuList 重新生成路由（不请求 API）
            const accessRoutes = await store.dispatch('menus/restoreRoutesFromCache');
            
            // 动态添加路由作为 Layout 的子路由
            accessRoutes.forEach(route => router.addRoute('Layout', route));
            
            // 动态路由加载完成后，添加404通配符路由
            add404Route();
            
            // 重新导航到目标页面
            next({ ...to, replace: true });
          } catch (error) {
            console.error('恢复路由失败:', error);
            // 如果恢复失败，重新加载菜单
            store.commit('menus/setHasLoaded', false);
            next({ ...to, replace: true });
          }
        } else {
          // 确保404通配符路由已添加
          add404Route();
          next();
        }
      }
    }
  } else {
    // 未登录
    if (whiteList.includes(to.path)) {
      next();
    } else {
      next('/login');
      NProgress.done();
    }
  }
});

router.afterEach(() => {
  NProgress.done();
});

export default router;
